// Operator › Failed events (`/operator/consumers`; coverage audit §5 row 8, rows 86-88; Reports /
// Ops UX pass 2026-10-21): the event consumers' records that need an operator
// (`GET /v1/operator/consumers`), one 36 px row each with Retry and Skip as visible buttons
// (Retry replays the event in order; Skip asks for a reason, `skipped_by_operator`, audited), and
// the row opens a drawer with the error and the record. The 40 px toolbar filters by state.
// One call on load.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerSection,
  DrawerTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  ListRow,
  LocationHeader,
  Notice,
  Segmented,
  Textarea,
  Toolbar,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleCheckIcon, TriangleAlertIcon } from "lucide-react";
import { type FormEvent, useId, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type { ConsumerRecord, MeResponse } from "../../api/types";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { OpsFrame, operatorCrumb } from "../ops-ux";
import { keys, operatorConsumersQuery } from "../queries";
import { Facts, LoadError, Loading } from "../screen-parts";
import { useListKeys } from "../work/list-keys";

const STATE: Record<
  ConsumerRecord["state"],
  { label: string; variant: "danger" | "warning" | "success" | "neutral" }
> = {
  failed: { label: "Failed", variant: "danger" },
  retrying: { label: "Retrying", variant: "warning" },
  done: { label: "Done", variant: "success" },
  skipped_by_operator: { label: "Skipped", variant: "neutral" },
};

const FILTERS = ["failed", "all"] as const;
type Filter = (typeof FILTERS)[number];

const recordKey = (r: ConsumerRecord) => `${r.consumer_name}:${r.company_id}:${r.event_id}`;

export function ConsumersPage({ api, me }: { api: Api; me: MeResponse }) {
  const consumers = useQuery(operatorConsumersQuery(api));
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("failed");
  const companyName = (id: string) =>
    me.agency?.grants.find((g) => g.company_id === id)?.company_name ?? `…${id.slice(-6)}`;
  const rows = consumers.data?.data ?? [];
  const selected = rows.find((r) => recordKey(r) === open) ?? null;
  const failed = rows.filter((r) => r.state === "failed").length;
  const shown = filter === "all" ? rows : rows.filter((r) => r.state === "failed");
  const listRef = useRef<HTMLDivElement>(null);
  useListKeys(listRef, {
    onOpen: (row) => {
      const key = row.dataset.recordKey;
      if (key) setOpen(key);
    },
  });

  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Failed events" }]}
        icon={TriangleAlertIcon}
        title="Failed events"
        count={consumers.isPending ? undefined : failed}
      />
      <Toolbar bleed>
        <Segmented
          size="sm"
          aria-label="State"
          value={filter}
          onValueChange={setFilter}
          options={[
            { value: "failed", label: `Need an operator · ${failed}` },
            { value: "all", label: `All · ${rows.length}` },
          ]}
        />
      </Toolbar>
      {consumers.isPending ? (
        <div className="px-6 py-4 max-md:px-4">
          <Loading label="Loading failed events" />
        </div>
      ) : consumers.isError ? (
        <div className="px-6 py-4 max-md:px-4">
          <LoadError what="Failed events" error={consumers.error} />
        </div>
      ) : shown.length === 0 ? (
        <div className="px-6 py-4 max-md:px-4">
          <EmptyState
            variant="card"
            icon={CircleCheckIcon}
            title="No failed events"
            description="Every consumer is keeping up. Records that fail after their retries show up here."
          />
        </div>
      ) : (
        <div ref={listRef} data-slot="consumers">
          {shown.map((r) => {
            const state = STATE[r.state];
            return (
              <ListRow
                key={recordKey(r)}
                data-list-row=""
                data-record={r.event_id}
                data-record-key={recordKey(r)}
                data-state={r.state}
                lead={<TriangleAlertIcon aria-hidden="true" />}
                title={`${r.consumer_name} · ${companyName(r.company_id)}`}
                onSelect={() => setOpen(recordKey(r))}
                selected={open === recordKey(r)}
                chips={<Badge variant={state.variant}>{state.label}</Badge>}
                meta={`${r.attempts} ${r.attempts === 1 ? "attempt" : "attempts"} · ${formatDateTime(r.processed_at)}`}
                trailing={
                  r.state === "failed" ? (
                    <InlineActions api={api} record={r} companyName={companyName(r.company_id)} />
                  ) : null
                }
              />
            );
          })}
        </div>
      )}
      <Drawer open={selected !== null} onOpenChange={(o) => !o && setOpen(null)} width="640px">
        {selected ? (
          <ConsumerDetail
            api={api}
            record={selected}
            companyName={companyName(selected.company_id)}
            onDone={() => setOpen(null)}
          />
        ) : null}
      </Drawer>
    </OpsFrame>
  );
}

function useConsumerActions(api: Api, record: ConsumerRecord, onDone?: () => void) {
  const qc = useQueryClient();
  const ref = { company_id: record.company_id, event_id: record.event_id };
  const refresh = () => qc.invalidateQueries({ queryKey: keys.operatorConsumers() });
  const retry = useMutation({
    mutationFn: () =>
      api.client.operator.consumers.retry({
        params: { name: record.consumer_name },
        idempotencyKey: newIdempotencyKey(),
        body: ref,
      }),
    onSuccess: async () => {
      await refresh();
      onDone?.();
    },
  });
  const skip = useMutation({
    mutationFn: (reason: string) =>
      api.client.operator.consumers.skip({
        params: { name: record.consumer_name },
        idempotencyKey: newIdempotencyKey(),
        body: { ...ref, reason },
      }),
    onSuccess: async () => {
      await refresh();
      onDone?.();
    },
  });
  return { retry, skip };
}

/** The row's visible Retry and Skip (Skip asks for its reason in a dialog). */
function InlineActions({
  api,
  record,
  companyName,
}: {
  api: Api;
  record: ConsumerRecord;
  companyName: string;
}) {
  const { retry, skip } = useConsumerActions(api, record);
  const [skipping, setSkipping] = useState(false);
  const busy = retry.isPending || skip.isPending;
  return (
    <span className="flex items-center gap-1" data-slot="consumer-actions">
      {/* A refused retry arrives as the problem toast (the client's mapping). */}
      <Button variant="secondary" size="xs" disabled={busy} onClick={() => retry.mutate()}>
        {retry.isPending ? "Retrying…" : "Retry"}
      </Button>
      <Button variant="ghost" size="xs" disabled={busy} onClick={() => setSkipping(true)}>
        Skip
      </Button>
      <SkipDialog
        open={skipping}
        onOpenChange={setSkipping}
        what={`${record.consumer_name} · ${companyName}`}
        pending={skip.isPending}
        error={skip.error}
        onSkip={(reason) => skip.mutate(reason, { onSuccess: () => setSkipping(false) })}
      />
    </span>
  );
}

function SkipDialog({
  open,
  onOpenChange,
  what,
  pending,
  error,
  onSkip,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  what: string;
  pending: boolean;
  error: unknown;
  onSkip: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const id = useId();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim()) onSkip(reason.trim());
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setReason("");
        onOpenChange(o);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>Skip this event?</DialogTitle>
            <DialogDescription>
              {what} will never process it; the reason is recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={id}>Reason</FieldLabel>
            <Textarea
              id={id}
              rows={2}
              maxLength={2000}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {error ? (
              <FieldError>{problemText(error, "The record could not be skipped.")}</FieldError>
            ) : null}
          </Field>
          <DialogFooter>
            <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
            <Button type="submit" variant="destructive" disabled={pending || !reason.trim()}>
              {pending ? "Skipping…" : "Skip event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConsumerDetail({
  api,
  record,
  companyName,
  onDone,
}: {
  api: Api;
  record: ConsumerRecord;
  companyName: string;
  onDone: () => void;
}) {
  const { retry, skip } = useConsumerActions(api, record, onDone);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const actionable = record.state === "failed";
  const busy = retry.isPending || skip.isPending;
  return (
    <>
      <DrawerHeader>Failed events › {record.consumer_name}</DrawerHeader>
      <DrawerBody>
        <DrawerTitle>{record.consumer_name}</DrawerTitle>
        <p className="mt-1 text-sm text-muted-foreground">
          {companyName} · {STATE[record.state].label}
        </p>
        <DrawerSection title="Error" className="mt-6">
          <pre className="rounded-md border border-border bg-sunken p-3 text-xs break-words whitespace-pre-wrap">
            {record.last_error ?? "No error recorded."}
          </pre>
        </DrawerSection>
        <DrawerSection title="Record">
          <Facts
            items={[
              { label: "Company", value: companyName },
              { label: "Event", value: <code className="text-xs">{record.event_id}</code> },
              { label: "Event sequence", value: record.event_seq ?? "—" },
              { label: "Attempts", value: record.attempts },
              { label: "Last processed", value: formatDateTime(record.processed_at) },
              {
                label: "Next attempt",
                value: record.next_attempt_at ? formatDateTime(record.next_attempt_at) : "None",
              },
            ]}
          />
        </DrawerSection>
        {actionable ? (
          <>
            <DrawerSection title="Retry">
              <p className="mb-3 text-sm text-muted-foreground">
                Runs the consumer on this event again, in event order. Use it once the cause is
                fixed.
              </p>
              {retry.isError ? (
                <Notice tone="danger" className="mb-3" role="alert">
                  {problemText(retry.error, "The record could not be retried.")}
                </Notice>
              ) : null}
              <Button disabled={busy} onClick={() => retry.mutate()}>
                {retry.isPending ? "Retrying…" : "Retry"}
              </Button>
            </DrawerSection>
            <DrawerSection title="Skip">
              <Field>
                <FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
                <Textarea
                  id={reasonId}
                  rows={2}
                  maxLength={2000}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <FieldDescription>
                  The consumer will never process this event; the reason is recorded.
                </FieldDescription>
                {skip.isError ? (
                  <FieldError>
                    {problemText(skip.error, "The record could not be skipped.")}
                  </FieldError>
                ) : null}
              </Field>
              <Button
                variant="destructive"
                className="mt-3"
                disabled={busy || !reason.trim()}
                onClick={() => skip.mutate(reason.trim())}
              >
                {skip.isPending ? "Skipping…" : "Skip event"}
              </Button>
            </DrawerSection>
          </>
        ) : (
          <Notice tone="neutral" className="mt-6">
            Nothing to do: the record is {STATE[record.state].label.toLowerCase()}.
          </Notice>
        )}
      </DrawerBody>
    </>
  );
}
